import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Puzzle, Download, Power, PowerOff, Trash2, ExternalLink, RefreshCw, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { usePlugins } from "@/plugins/PluginContext";
import type { InstalledPlugin } from "@/plugins/types";

type StoreEntry = {
  repo: string;
  name: string;
  description: string | null;
  homepage: string;
  latestTag: string | null;
  publishedAt: string | null;
  zipUrl: string | null;
  zipName: string | null;
};

function PermissionBadges({ permissions }: { permissions: string[] }) {
  if (!permissions.length) return <span className="text-sm text-muted-foreground">None</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {permissions.map((perm) => (
        <Badge key={perm} variant={perm.startsWith("network:") ? "default" : "secondary"}>
          {perm}
        </Badge>
      ))}
    </div>
  );
}

export default function PluginStore() {
  const { toast } = useToast();
  const {
    installed,
    loaded,
    loadErrors,
    activate,
    deactivate,
    uninstall,
    managerFetch,
    refetchInstalled,
  } = usePlugins();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmInstall, setConfirmInstall] = useState<{
    entry: StoreEntry;
    installedVersion?: string;
  } | null>(null);
  const [confirmUninstall, setConfirmUninstall] = useState<InstalledPlugin | null>(null);

  const {
    data: store,
    isLoading: storeLoading,
    error: storeError,
    refetch: refetchStore,
  } = useQuery<{ owner: string; plugins: StoreEntry[]; error?: string }>({
    queryKey: ["plugin-store"],
    queryFn: async () => {
      const res = await managerFetch("GET", "/api/plugins/store");
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? "Failed to load plugin store");
      return data;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const installedByRepo = new Map(
    installed.filter((p) => p.source_repo).map((p) => [p.source_repo as string, p])
  );

  const runInstall = async (entry: StoreEntry) => {
    if (!entry.zipUrl) return;
    setBusyId(entry.repo);
    try {
      const res = await managerFetch("POST", "/api/plugins/install", {
        zipUrl: entry.zipUrl,
        repo: entry.repo,
        tag: entry.latestTag,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? "Install failed");
      await refetchInstalled();
      toast({
        title: "Plugin installed",
        description: `${data.plugin?.name ?? entry.name} ${data.plugin?.version ?? ""} installed. Activate it to start using it.`,
      });
    } catch (e) {
      toast({
        title: "Install failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
      setConfirmInstall(null);
    }
  };

  const runToggle = async (plugin: InstalledPlugin) => {
    setBusyId(plugin.plugin_id);
    try {
      if (plugin.status === "active") {
        await deactivate(plugin.plugin_id);
        toast({ title: "Plugin deactivated", description: `${plugin.name} is now inactive.` });
      } else {
        await activate(plugin.plugin_id);
        toast({ title: "Plugin activated", description: `${plugin.name} is now active.` });
      }
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const runUninstall = async (plugin: InstalledPlugin) => {
    setBusyId(plugin.plugin_id);
    try {
      await uninstall(plugin.plugin_id);
      toast({ title: "Plugin removed", description: `${plugin.name} has been uninstalled.` });
    } catch (e) {
      toast({
        title: "Uninstall failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
      setConfirmUninstall(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-6 flex items-center gap-3">
        <Puzzle className="h-7 w-7 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Plugins</h1>
          <p className="text-sm text-muted-foreground">
            Extend Deeply Smart with plugins — install, activate, and manage them here.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link href="/plugins/docs">
            <BookOpen className="h-4 w-4 mr-1" /> Build a plugin
          </Link>
        </Button>
      </div>

      <Tabs defaultValue="installed">
        <TabsList>
          <TabsTrigger value="installed">Installed ({installed.length})</TabsTrigger>
          <TabsTrigger value="store">Plugin Store</TabsTrigger>
        </TabsList>

        <TabsContent value="installed" className="mt-4 space-y-3">
          {installed.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                No plugins installed yet. Browse the Plugin Store tab to install one.
              </CardContent>
            </Card>
          )}
          {installed.map((plugin) => {
            const isActive = plugin.status === "active";
            const isLoaded = loaded.some((p) => p.plugin.plugin_id === plugin.plugin_id);
            const loadError = loadErrors[plugin.plugin_id];
            return (
              <Card key={plugin.plugin_id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        {plugin.name}
                        <Badge variant="outline">v{plugin.version}</Badge>
                        <Badge variant={isActive ? "default" : "secondary"}>
                          {isActive ? (isLoaded ? "Active" : loadError ? "Load error" : "Loading…") : "Inactive"}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {plugin.manifest.description || "No description"}
                        {plugin.source_repo && (
                          <span className="ml-2 text-xs">({plugin.source_repo})</span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        size="sm"
                        variant={isActive ? "outline" : "default"}
                        disabled={busyId === plugin.plugin_id}
                        onClick={() => runToggle(plugin)}
                        data-testid={`button-plugin-toggle-${plugin.plugin_id}`}
                      >
                        {isActive ? (
                          <>
                            <PowerOff className="mr-1.5 h-4 w-4" /> Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="mr-1.5 h-4 w-4" /> Activate
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        disabled={busyId === plugin.plugin_id}
                        onClick={() => setConfirmUninstall(plugin)}
                        data-testid={`button-plugin-uninstall-${plugin.plugin_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {loadError && isActive && (
                    <p className="mb-2 text-sm text-destructive">{loadError}</p>
                  )}
                  <div className="text-xs text-muted-foreground">
                    <span className="mr-2 font-medium">Permissions:</span>
                    <PermissionBadges permissions={(plugin.manifest.permissions ?? []) as string[]} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="store" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {store?.owner
                ? `Plugins from github.com/${store.owner} (topic: deeplysmart-plugin)`
                : "Plugins released on your GitHub account"}
            </p>
            <Button size="sm" variant="ghost" onClick={() => refetchStore()}>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
          </div>

          {storeLoading && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                Loading plugin store…
              </CardContent>
            </Card>
          )}
          {storeError && (
            <Card>
              <CardContent className="py-10 text-center">
                <p className="text-destructive">{(storeError as Error).message}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Set PLUGIN_GITHUB_OWNER (and optionally GITHUB_PLUGINS_TOKEN) on the server.
                </p>
              </CardContent>
            </Card>
          )}
          {store?.error && !storeError && !storeLoading && (
            <Card>
              <CardContent className="py-10 text-center">
                <p className="text-destructive">{store.error}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Check GITHUB_PLUGINS_TOKEN on the server — it must be a PAT from the plugin
                  owner with read access to private repos.
                </p>
              </CardContent>
            </Card>
          )}
          {store?.plugins?.length === 0 && !storeLoading && !store?.error && !storeError && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                No repos with the “deeplysmart-plugin” topic found for {store.owner}.
              </CardContent>
            </Card>
          )}
          {store?.plugins?.map((entry) => {
            const installedPlugin = installedByRepo.get(entry.repo);
            const upToDate =
              installedPlugin && entry.latestTag && installedPlugin.source_tag === entry.latestTag;
            return (
              <Card key={entry.repo}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        {entry.name}
                        {entry.latestTag && <Badge variant="outline">{entry.latestTag}</Badge>}
                        {installedPlugin && (
                          <Badge variant="secondary">
                            Installed{upToDate ? "" : ` (v${installedPlugin.version})`}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {entry.description || "No description"}
                      </CardDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button size="sm" variant="ghost" asChild>
                        <a href={entry.homepage} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        size="sm"
                        disabled={!entry.zipUrl || busyId === entry.repo || !!upToDate}
                        onClick={() =>
                          setConfirmInstall({ entry, installedVersion: installedPlugin?.version })
                        }
                        data-testid={`button-plugin-install-${entry.name}`}
                      >
                        <Download className="mr-1.5 h-4 w-4" />
                        {upToDate ? "Up to date" : installedPlugin ? "Update" : "Install"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {!entry.zipUrl && (
                  <CardContent className="pt-0">
                    <p className="text-xs text-muted-foreground">
                      No release zip found — publish a GitHub Release with a .zip asset.
                    </p>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Install consent dialog */}
      <Dialog open={!!confirmInstall} onOpenChange={(open) => !open && setConfirmInstall(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmInstall?.installedVersion ? "Update" : "Install"} {confirmInstall?.entry.name}?
            </DialogTitle>
            <DialogDescription>
              {confirmInstall?.entry.repo} {confirmInstall?.entry.latestTag ?? ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              This plugin runs with your account and can use the capabilities it declares. Only
              install plugins you trust.
            </p>
            <p className="text-muted-foreground">
              The permissions it requests are shown on the Installed tab after installation and
              enforced by the plugin gateway for network access.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmInstall(null)}>
              Cancel
            </Button>
            <Button
              disabled={busyId === confirmInstall?.entry.repo}
              onClick={() => confirmInstall && runInstall(confirmInstall.entry)}
            >
              {busyId === confirmInstall?.entry.repo ? "Installing…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Uninstall confirm dialog */}
      <Dialog open={!!confirmUninstall} onOpenChange={(open) => !open && setConfirmUninstall(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {confirmUninstall?.name}?</DialogTitle>
            <DialogDescription>
              This deletes the plugin, its settings, and its stored secrets. Data it saved to your
              knowledge base or RAG index is kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmUninstall(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busyId === confirmUninstall?.plugin_id}
              onClick={() => confirmUninstall && runUninstall(confirmUninstall)}
            >
              {busyId === confirmUninstall?.plugin_id ? "Removing…" : "Uninstall"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
